// components/LSSF/ContactEnquiriesList.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdDelete, MdSearch, MdVisibility } from 'react-icons/md';
import { Link } from 'react-router-dom';

const ContactEnquiriesList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const limit = 20;

  const getEnquiries = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/contact`);

      setData(res.data.data);
      setFilteredData(res.data.data);
      const total = Math.ceil(res.data.count / limit);
      setTotalPages(total > 0 ? total : 1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching enquiries:', err);
      toast.error('Failed to fetch enquiries');
      setLoading(false);
    }
  };

  useEffect(() => {
    getEnquiries();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.filter(item => {
        return (
          (item.first_name && item.first_name.toLowerCase().includes(searchLower)) ||
          (item.last_name && item.last_name.toLowerCase().includes(searchLower)) ||
          (item.email && item.email.toLowerCase().includes(searchLower)) ||
          (item.phone && item.phone.toString().includes(searchLower)) ||
          (item.description && item.description.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const deleteEnquiry = async (id) => {
    const choice = window.confirm('Are you sure you want to delete this enquiry?');
    if (choice) {
      try {
        await axios.delete(`${base_url}/api/v1/enquiries/${id}`);
        toast.success('Enquiry deleted successfully');
        getEnquiries();
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete enquiry');
      }
    }
  };

  const viewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setShowModal(true);
  };

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-4">
                  <h4 className="mb-0">Contact Enquiries</h4>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Description</th>
                        <th>Created At</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          {[...Array(7)].map((_, i) => (
                            <td key={i}><Skeleton count={1} /></td>
                          ))}
                        </tr>
                      ) : paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{item.first_name} {item.last_name}</td>
                            <td>{item.email}</td>
                            <td>{item.phone}</td>
                            <td>
                              {item.description?.substring(0, 50)}
                              {item.description?.length > 50 && '...'}
                            </td>
                            <td>{new Date(item.created_at).toLocaleDateString()}</td>
                            <td>
                              <button 
                                className="btn btn-sm btn-info me-2"
                                onClick={() => viewDetails(item)}
                              >
                                <MdVisibility size={18} /> View
                              </button>
                              {/* <button 
                                className="btn btn-sm btn-danger"
                                onClick={() => deleteEnquiry(item.id)}
                              >
                                <MdDelete size={18} /> Delete
                              </button> */}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger">
                          <td colSpan={7}>No enquiries found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {filteredData.length > limit && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </button>
                    <span>Page {page} of {totalPages}</span>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={page === totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Viewing Enquiry Details */}
      {showModal && selectedEnquiry && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Enquiry Details</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>First Name:</strong>
                    <p>{selectedEnquiry.first_name}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Last Name:</strong>
                    <p>{selectedEnquiry.last_name}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Email:</strong>
                    <p>{selectedEnquiry.email}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Phone:</strong>
                    <p>{selectedEnquiry.phone}</p>
                  </div>
                  <div className="col-12 mb-3">
                    <strong>Description:</strong>
                    <p>{selectedEnquiry.description}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Created At:</strong>
                    <p>{new Date(selectedEnquiry.created_at).toLocaleString()}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>ID:</strong>
                    <p>{selectedEnquiry.id}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default ContactEnquiriesList;